package org.teddapp.project.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

/**
 * Surface du design system : [TeddTokens.Surface] contourée d'un filet, posée sur le papier.
 * La séparation vient des surfaces, pas des ombres.
 *
 * `tonal` donne une zone en retrait ([TeddTokens.SurfaceSunken]), sans contour.
 * `elevation` est réservé à ce qui flotte au-dessus du contenu (calendrier, feuille).
 */
@Composable
fun Modifier.teddSurface(
    shape: Shape = RoundedCornerShape(TeddTokens.RLg),
    elevation: Dp = 0.dp,
    tonal: Boolean = false,
): Modifier {
    val colors = LocalTeddColors.current
    if (tonal) return this.clip(shape).background(colors.surfaceSunken, shape)
    val lifted = if (elevation > 0.dp) teddOverlayShadow(shape) else teddCardShadow(shape)
    return lifted.clip(shape).background(colors.surface, shape).border(1.dp, colors.line, shape)
}

/** Ton d'une carte. */
enum class TeddCardTone {
    /** Surface contourée, ombre de carte. */
    Default,
    /** Zone en retrait, sans contour. */
    Sunken,
    /** Aplat d'encre, pour le seul bloc qui doit dominer l'écran. */
    Ink,
}

/**
 * Carte ou tuile. Avec un [title], elle porte un en-tête : l'icône du module dans sa
 * couleur de repère, le titre, puis une [action] à droite.
 *
 * La couleur d'un module ne teinte que son icône : jamais le fond, jamais le titre.
 */
@Composable
fun TeddCard(
    modifier: Modifier = Modifier,
    shape: Shape = RoundedCornerShape(TeddTokens.RLg),
    padding: PaddingValues = PaddingValues(TeddSpace.S5),
    elevation: Dp = 0.dp,
    tonal: Boolean = false,
    tone: TeddCardTone = if (tonal) TeddCardTone.Sunken else TeddCardTone.Default,
    title: String? = null,
    icon: String? = null,
    iconColor: Color = TeddTokens.InkMuted,
    action: (@Composable () -> Unit)? = null,
    content: @Composable ColumnScope.() -> Unit,
) {
    val colors = LocalTeddColors.current
    val surface = when (tone) {
        TeddCardTone.Default -> Modifier.teddSurface(shape = shape, elevation = elevation)
        TeddCardTone.Sunken -> Modifier.teddSurface(shape = shape, tonal = true)
        TeddCardTone.Ink -> Modifier.clip(shape).background(colors.ink, shape)
    }
    Column(
        modifier = modifier.then(surface).padding(padding),
        verticalArrangement = if (title != null) Arrangement.spacedBy(TeddSpace.S4) else Arrangement.Top,
    ) {
        if (title != null) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
            ) {
                if (icon != null) {
                    Box(
                        modifier = Modifier
                            .size(28.dp)
                            .background(
                                if (tone == TeddCardTone.Sunken) colors.surface else colors.surfaceSunken,
                                RoundedCornerShape(TeddTokens.RSm),
                            ),
                        contentAlignment = Alignment.Center,
                    ) {
                        TeddIcon(name = icon, size = 16.dp, color = iconColor)
                    }
                }
                Text(
                    text = title,
                    modifier = Modifier.weight(1f),
                    color = if (tone == TeddCardTone.Ink) colors.bg else colors.ink,
                    style = TeddType.Title2.copy(fontSize = 18.sp, lineHeight = 24.sp),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                if (action != null) action()
            }
        }
        content()
    }
}
